import type { IdentityProviderRecord } from './identityProviderTypes';
import type { EntraFinishResult } from './entraConnectionService';

export type EntraWizardStep = 1 | 2 | 3 | 4 | 5;

export type EntraUserScope = 'all' | 'groups' | 'selected';
export type EntraPortalAccess = 'none' | 'default' | 'from-group';
export type EntraDisabledBehavior = 'suspend' | 'none';
export type EntraDeletedBehavior = 'inactive' | 'none' | 'delete';
export type EntraSsoEnforcement = 'optional' | 'directory-users' | 'tenant';
export type EntraUserMatchMode = 'email' | 'approval';

export interface EntraMockGroup {
  id: string;
  name: string;
  memberCount: number;
}

export interface EntraMockUser {
  id: string;
  email: string;
}

export type EntraTestStatus = 'idle' | 'testing' | 'success' | 'auth_failed' | 'permissions_missing';

export interface EntraGraphAccessState {
  users: { ok: boolean; message: string };
  groups: { ok: boolean; message: string };
  groupMemberships: { ok: boolean; message: string };
}

export interface EntraWizardState {
  connected: boolean;
  connectionId: string;
  organization: string;
  tenantId: string;
  clientId: string;
  clientSecret: string;
  secretExpiration: string;
  microsoftTenantId: string;
  configuredBy: string;
  graphAccessVerified: boolean;
  graphAccess: EntraGraphAccessState;
  testStatus: EntraTestStatus;
  testMessage: string;
  connectedAt: string;
  autoSync: boolean;
  userScope: EntraUserScope;
  selectedGroupIds: string[];
  selectedUserIds: string[];
  groupSearch: string;
  userSearch: string;
  syncGroups: boolean;
  syncGroupMemberships: boolean;
  attributes: {
    firstName: boolean;
    lastName: boolean;
    email: boolean;
    department: boolean;
    jobTitle: boolean;
    manager: boolean;
    employeeId: boolean;
    officeLocation: boolean;
    accountStatus: boolean;
  };
  importNewUsers: boolean;
  portalAccess: EntraPortalAccess;
  disabledBehavior: EntraDisabledBehavior;
  deletedBehavior: EntraDeletedBehavior;
  lastSync: string;
  syncStatus: string;
  ssoEnabled: boolean;
  ssoEnforcement: EntraSsoEnforcement;
  userMatchMode: EntraUserMatchMode;
  ssoTested: boolean;
}

export const ENTRA_WIZARD_STEPS: { id: EntraWizardStep; title: string; subtitle: string }[] = [
  { id: 1, title: 'Overview', subtitle: 'What will be configured' },
  { id: 2, title: 'Connection', subtitle: 'App Registration' },
  { id: 3, title: 'Sync Configuration', subtitle: 'Choose what to sync' },
  { id: 4, title: 'Single Sign-On', subtitle: 'OIDC sign-in settings' },
  { id: 5, title: 'Review', subtitle: 'Confirm and finish' },
];

export const MOCK_ENTRA_GROUPS: EntraMockGroup[] = [
  { id: 'g-ai', name: 'AI Platform Users', memberCount: 245 },
  { id: 'g-eng', name: 'Engineering', memberCount: 832 },
  { id: 'g-con', name: 'Contractors', memberCount: 1492 },
];

export const MOCK_ENTRA_USERS: EntraMockUser[] = [
  { id: 'u-ada', email: 'ada@selfic.ai' },
  { id: 'u-sam', email: 'sam@selfic.ai' },
  { id: 'u-lee', email: 'lee@selfic.ai' },
];

export const ATTRIBUTE_MAPPINGS: { entra: string; selfic: string }[] = [
  { entra: 'id', selfic: 'External User ID' },
  { entra: 'mail / userPrincipalName', selfic: 'Email' },
  { entra: 'givenName', selfic: 'First Name' },
  { entra: 'surname', selfic: 'Last Name' },
  { entra: 'department', selfic: 'Department' },
  { entra: 'jobTitle', selfic: 'Job Title' },
  { entra: 'accountEnabled', selfic: 'Directory Status' },
  { entra: 'manager', selfic: 'Manager' },
];

export function createDefaultEntraWizardState(existing?: IdentityProviderRecord | null): EntraWizardState {
  const alreadyConnected = existing?.status === 'connected';
  return {
    connected: alreadyConnected,
    connectionId: existing?.connectionId || '',
    organization: existing?.organization || '',
    tenantId: existing?.tenantId || '',
    clientId: existing?.clientId || '',
    clientSecret: '',
    secretExpiration: '',
    microsoftTenantId: existing?.tenantId || '',
    configuredBy: existing?.configuredBy || '',
    graphAccessVerified: existing?.graphAccessVerified ?? alreadyConnected,
    graphAccess: {
      users: { ok: alreadyConnected, message: alreadyConnected ? 'Access verified' : 'Not verified' },
      groups: { ok: alreadyConnected, message: alreadyConnected ? 'Access verified' : 'Not verified' },
      groupMemberships: { ok: alreadyConnected, message: alreadyConnected ? 'Access verified' : 'Not verified' },
    },
    testStatus: alreadyConnected ? 'success' : 'idle',
    testMessage: '',
    connectedAt: alreadyConnected ? 'Previously connected' : '',
    autoSync: existing?.autoSync ?? true,
    userScope: 'all',
    selectedGroupIds: ['g-ai', 'g-eng'],
    selectedUserIds: [],
    groupSearch: '',
    userSearch: '',
    syncGroups: true,
    syncGroupMemberships: true,
    attributes: {
      firstName: true,
      lastName: true,
      email: true,
      department: true,
      jobTitle: true,
      manager: true,
      employeeId: true,
      officeLocation: true,
      accountStatus: true,
    },
    importNewUsers: true,
    portalAccess: 'none',
    disabledBehavior: 'suspend',
    deletedBehavior: 'inactive',
    lastSync: existing?.lastSync || 'Never',
    syncStatus: 'Ready',
    ssoEnabled: existing?.ssoEnabled ?? false,
    ssoEnforcement: 'optional',
    userMatchMode: 'email',
    ssoTested: false,
  };
}

export function toEntraProviderRecord(
  state: EntraWizardState,
  existing?: IdentityProviderRecord | null,
  finish?: Pick<EntraFinishResult, 'usersCreated' | 'usersUpdated' | 'rolesCreatedOrUpdated'> | null,
): IdentityProviderRecord {
  const userCount = finish
    ? finish.usersCreated + finish.usersUpdated
    : existing?.userCount;
  const groupCount = finish ? finish.rolesCreatedOrUpdated : existing?.groupCount;
  return {
    id: existing?.id || state.connectionId || 'entra-local',
    kind: 'entra',
    name: 'Microsoft Entra ID',
    status: 'connected',
    userCount,
    groupCount,
    autoSync: state.autoSync,
    lastSync: finish ? 'Just now' : existing?.lastSync || 'Never',
    ssoEnabled: state.ssoEnabled,
    features: state.ssoEnabled
      ? ['Directory', 'Users', 'Groups', 'SSO']
      : ['Directory', 'Users', 'Groups'],
    tenantId: state.microsoftTenantId || state.tenantId,
    clientId: state.clientId,
    organization: state.organization,
    configuredBy: state.configuredBy,
    graphAccessVerified: state.graphAccessVerified,
    connectionId: state.connectionId,
  };
}
